import { Skeleton } from "@bednbody/ui/components/skeleton";
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { BookingStatsRange, DailyBookingStat } from "../ops-dashboard.hooks";

const pesos = new Intl.NumberFormat("en-PH", {
  style: "currency",
  currency: "PHP",
  maximumFractionDigits: 0,
});

/**
 * Owner-level view of volume and value in one place. Revenue is deliberately
 * defined as the value of completed sessions, matching Analytics; payment
 * settlement remains the PayMongo ledger's responsibility.
 */
export function BusinessPerformanceChart({
  period,
  range,
  isLoading = false,
}: {
  period: DailyBookingStat[];
  range: BookingStatsRange;
  isLoading?: boolean;
}) {
  if (isLoading) {
    return <Skeleton className="h-72 motion-reduce:animate-none" />;
  }

  const hasActivity = period.some((bucket) => bucket.bookings > 0);
  if (!hasActivity) {
    return (
      <div className="flex h-72 items-center justify-center text-sm text-muted-foreground">
        No booking activity {range === "today" ? "today" : `in the last ${range} days`}.
      </div>
    );
  }

  const totals = period.reduce(
    (summary, bucket) => ({
      bookings: summary.bookings + bucket.bookings,
      bookedValue: summary.bookedValue + bucket.bookedValue,
      revenue: summary.revenue + bucket.revenue,
    }),
    { bookings: 0, bookedValue: 0, revenue: 0 },
  );

  return (
    <div>
      <p className="mb-3 text-xs text-muted-foreground">
        {range === "today"
          ? "Hourly view of today’s scheduled work"
          : `Daily view of the last ${range} days`}
      </p>
      <dl className="mb-5 grid grid-cols-3 divide-x divide-border rounded-xl border border-border bg-background/60">
        <div className="px-3 py-2.5">
          <dt className="type-body-micro font-medium uppercase tracking-wide text-muted-foreground">
            Bookings
          </dt>
          <dd className="mt-1 text-base font-semibold tabular-nums text-foreground">
            {totals.bookings}
          </dd>
        </div>
        <div className="px-3 py-2.5">
          <dt className="type-body-micro font-medium uppercase tracking-wide text-muted-foreground">
            Booked value
          </dt>
          <dd className="mt-1 text-base font-semibold tabular-nums text-foreground">
            {pesos.format(totals.bookedValue)}
          </dd>
        </div>
        <div className="px-3 py-2.5">
          <dt className="type-body-micro font-medium uppercase tracking-wide text-muted-foreground">
            Revenue
          </dt>
          <dd className="mt-1 text-base font-semibold tabular-nums text-foreground">
            {pesos.format(totals.revenue)}
          </dd>
        </div>
      </dl>

      <div className="h-60">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={period} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
            <defs>
              <linearGradient id="business-booked-value" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--chart-1)" stopOpacity={0.3} />
                <stop offset="100%" stopColor="var(--chart-1)" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid
              vertical={false}
              stroke="var(--chart-grid)"
              strokeDasharray="3 5"
              opacity={0.65}
            />
            <XAxis
              dataKey="day"
              axisLine={false}
              interval={range === "today" ? 2 : "preserveStartEnd"}
              minTickGap={range === 30 ? 20 : 0}
              tickLine={false}
              tick={{ fill: "var(--muted-foreground)", fontSize: 12 }}
            />
            <YAxis yAxisId="bookings" hide />
            <YAxis yAxisId="money" hide />
            <Tooltip
              contentStyle={{
                borderColor: "var(--border)",
                borderRadius: "0.75rem",
                background: "var(--popover)",
              }}
              labelStyle={{ color: "var(--foreground)" }}
              formatter={(value, name) => [
                name === "Bookings" ? Number(value).toLocaleString() : pesos.format(Number(value)),
                name,
              ]}
            />
            <Bar
              yAxisId="bookings"
              dataKey="bookings"
              name="Bookings"
              fill="var(--chart-4)"
              fillOpacity={0.82}
              radius={[5, 5, 2, 2]}
              maxBarSize={26}
            />
            <Area
              yAxisId="money"
              type="monotone"
              dataKey="bookedValue"
              name="Booked value"
              stroke="var(--chart-1)"
              strokeWidth={2}
              fill="url(#business-booked-value)"
              fillOpacity={1}
              activeDot={{ r: 4 }}
            />
            <Line
              yAxisId="money"
              type="monotone"
              dataKey="revenue"
              name="Revenue"
              stroke="var(--chart-2)"
              strokeWidth={2.25}
              dot={false}
              activeDot={{ r: 4, fill: "var(--chart-2)" }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-sm bg-chart-4/80" />
          Bookings
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-0.5 w-3 rounded-full bg-chart-1" />
          Booked value
        </span>
        <span className="flex items-center gap-1.5">
          <span className="h-0.5 w-3 rounded-full bg-chart-2" />
          Revenue
        </span>
      </div>

      <p className="mt-3 text-xs text-muted-foreground">
        Revenue is the value of completed sessions; it is not the settled-payment ledger.
      </p>
    </div>
  );
}
